/**
 * Where to send someone back to after they sign in.
 *
 * Without this, every login lands on the home page — including a login the
 * person only opened because they clicked a link that needed one. The email
 * that asked them to answer a counter-offer dropped them on a feed, and the
 * answer never came.
 *
 * `sessionStorage`, not `localStorage`: the destination is meaningful for this
 * tab and this visit. Kept across a reload, it would send someone somewhere
 * they had already dealt with days earlier.
 */

import { safeInAppPath } from '@/lib/safePath'

const KEY = '@memberfy/returnTo'

/**
 * Only same-origin paths are stored.
 *
 * The value ends up in `navigate()`, so an absolute URL here would be an open
 * redirect: a crafted link could park `https://evil.example` in the session and
 * the app would walk the person there right after they typed their password.
 * `safeInAppPath` resolves the value against the app's origin (SEC-294).
 */
const isSafePath = (path: string) => safeInAppPath(path) !== null

/** Records where to come back to. Defaults to the page being looked at now. */
export const rememberReturnTo = (path?: string): void => {
  if (typeof window === 'undefined') return
  const target = path ?? `${window.location.pathname}${window.location.search}`

  /* The home page is the fallback anyway — storing it only risks overwriting a
     real destination captured a moment earlier. */
  if (!isSafePath(target) || target === '/') return

  try {
    window.sessionStorage.setItem(KEY, target)
  } catch {
    /* Private mode, or storage disabled. The fallback still works. */
  }
}

/**
 * Reads the destination and forgets it, in one go.
 *
 * Reading without clearing would send the person back to the same place on
 * their next login, long after it stopped being where they were going.
 */
export const takeReturnTo = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const target = window.sessionStorage.getItem(KEY)
    window.sessionStorage.removeItem(KEY)
    return target && isSafePath(target) ? target : null
  } catch {
    return null
  }
}

export const clearReturnTo = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    /* Nothing to clear if storage is unavailable. */
  }
}
